import {
  OTHER_FIXTURE_LABEL,
  fixturesToFlag,
  indoorFixtureMatrix,
  setIndoorFixtureCount,
  setOtherFixtureCount,
  setOtherFixtureLabel,
  setUncheckedFixtureCount,
  type SurveyV2,
  type VocabularyEntry,
} from '@ghp/domain'
import {
  Badge,
  Button,
  Card,
  CountInput,
  InfoNote,
  Input,
  ToggleChip,
  ToggleChipGroup,
  VisuallyHidden,
} from '@ghp/ui'
import { Info, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import s from './SurveyForm.module.css'

/**
 * « Points d'eau » — la matrice intérieure de la fiche (#258) : 13 points d'eau × 6 niveaux,
 * Oui (raccordé aux EU) / Non, et la colonne « Non contrôlé ».
 *
 * ⚠️ **Une colonne à la fois**, choisie par pastille dans l'ordre de la fiche : les 13 colonnes
 * côte à côte ne tiennent pas sur la tablette à la taille d'un bouton tactile. La pastille porte le
 * total de sa colonne, pour que l'agent voie d'un coup d'œil les niveaux déjà comptés.
 *
 * Les lignes et colonnes viennent du **référentiel** (M17) : sans lui, rien à compter — l'écran le
 * dit plutôt que d'inventer une liste (M21).
 */
export interface IndoorFixturesProps {
  survey: SurveyV2
  vocabulary: readonly VocabularyEntry[]
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
  /** Les anomalies relevées : une case « Non » rappelle celles qui la désignent (#312). */
  anomalies?: readonly {
    fixture?: string | null
    fixtureLocation?: string | null
    fixtureIndex?: number | null
  }[]
  /**
   * « La grille qui signale » (maquette v3, 01/10/2026) : un point d'eau compté « Non » ne part
   * pas aux EU, c'est un défaut — la case propose de relever l'anomalie tant qu'aucune ne le
   * désigne. Sans rappel, rien n'est proposé.
   */
  onFlag?: (fixture: string, location: string) => void
}

/** La sentinelle de la colonne « Non contrôlé » — jamais écrite, le relevé la range à part. */
const UNCHECKED = ''

export function IndoorFixtures({
  survey,
  vocabulary,
  disabled,
  onChange,
  anomalies = [],
  onFlag,
}: IndoorFixturesProps) {
  const matrix = indoorFixtureMatrix(survey, vocabulary, { all: true })
  const other = matrix.other
  // Le premier niveau tant que l'agent n'a rien choisi — relu à chaque rendu : le référentiel peut
  // descendre après l'ouverture de l'onglet.
  const [chosen, setColumn] = useState<string | null>(null)
  const column = chosen ?? matrix.locations[0]?.code ?? UNCHECKED
  const at = matrix.locations.findIndex((location) => location.code === column)
  const location = matrix.locations[at]

  if (matrix.rows.length === 0) {
    return (
      <Card className={s.card}>
        <h2 className={s.title}>Points d’eau</h2>
        <InfoNote icon={<Info size={14} />}>
          La liste des points d’eau n’est pas encore descendue sur cette tablette : synchronisez-la
          pour les compter.
        </InfoNote>
      </Card>
    )
  }

  const total = (code: string) =>
    code === UNCHECKED
      ? (survey.uncheckedFixtures ?? []).reduce((sum, cell) => sum + cell.count, 0)
      : [...(survey.indoorFixtures ?? []), ...(survey.otherFixture?.cells ?? [])]
          .filter((cell) => cell.location === code)
          .reduce((sum, cell) => sum + cell.count, 0)
  const countOf = (value: number) => (value > 0 ? value : undefined)
  const toFlag = fixturesToFlag(survey, anomalies)

  return (
    <Card className={s.card}>
      <h2 className={s.title}>Points d’eau</h2>
      <ToggleChipGroup aria-label="Colonne de la fiche" className={s.columns}>
        {[{ code: UNCHECKED, label: 'Non contrôlé' }, ...matrix.locations].map((heading) => (
          <ToggleChip
            key={heading.code}
            pressed={heading.code === column}
            count={countOf(total(heading.code))}
            onPressedChange={() => setColumn(heading.code)}
          >
            {heading.label}
          </ToggleChip>
        ))}
      </ToggleChipGroup>

      <table className={s.matrix}>
        <thead>
          <tr>
            <th>Point d’eau</th>
            {location === undefined ? (
              <th className={s.count}>Non contrôlé</th>
            ) : (
              <>
                <th className={s.count}>Oui — raccordé EU</th>
                <th className={s.count}>Non</th>
                <th>
                  <VisuallyHidden>Anomalie</VisuallyHidden>
                </th>
              </>
            )}
          </tr>
        </thead>
        {/* Une clé par colonne : une frappe refusée dans un niveau ne passe pas au suivant. */}
        <tbody key={column}>
          {matrix.rows.map((row) => {
            const cell = location === undefined ? undefined : row.counts[at]
            const counted =
              location === undefined
                ? row.unchecked !== null
                : cell?.connected != null || cell?.notConnected != null

            return (
              <tr key={row.fixture.code} data-on={counted ? '' : undefined}>
                <td>{row.fixture.label}</td>
                {location === undefined ? (
                  <td className={s.count}>
                    <CountInput
                      label={`${row.fixture.label}, non contrôlé`}
                      value={row.unchecked}
                      disabled={disabled}
                      onValueChange={(count) =>
                        onChange?.({
                          ...survey,
                          uncheckedFixtures: setUncheckedFixtureCount(
                            survey.uncheckedFixtures,
                            row.fixture.code,
                            count,
                          ),
                        })
                      }
                    />
                  </td>
                ) : (
                  <>
                    {([true, false] as const).map((connected) => (
                      <td key={String(connected)} className={s.count}>
                        <CountInput
                          label={`${row.fixture.label}, ${location.label}, ${connected ? 'Oui' : 'Non'}`}
                          value={
                            connected ? (cell?.connected ?? null) : (cell?.notConnected ?? null)
                          }
                          disabled={disabled}
                          onValueChange={(count) =>
                            onChange?.({
                              ...survey,
                              indoorFixtures: setIndoorFixtureCount(
                                survey.indoorFixtures,
                                { fixture: row.fixture.code, location: location.code, connected },
                                count,
                              ),
                            })
                          }
                        />
                      </td>
                    ))}
                    <td>
                      <FlagCell
                        fixture={row.fixture.code}
                        location={location}
                        anomalies={anomalies}
                        toFlag={toFlag.some(
                          (waiting) =>
                            waiting.fixture === row.fixture.code &&
                            waiting.location === location.code,
                        )}
                        disabled={disabled}
                        onFlag={onFlag}
                        label={row.fixture.label}
                      />
                    </td>
                  </>
                )}
              </tr>
            )
          })}
          {/* La ligne « Autre » (#583, M45) : un point d'eau que la liste n'a pas, sous le libellé
              de l'agent. Ni « Non contrôlé », ni anomalie désignée depuis la grille. */}
          {location !== undefined && other !== null && (
            <tr
              data-on={
                other.counts[at]?.connected != null || other.counts[at]?.notConnected != null
                  ? ''
                  : undefined
              }
            >
              <td>
                <Input
                  aria-label="Autre point d’eau"
                  placeholder={OTHER_FIXTURE_LABEL}
                  defaultValue={survey.otherFixture?.label ?? ''}
                  disabled={disabled}
                  onValueChange={(label) =>
                    onChange?.({
                      ...survey,
                      otherFixture: setOtherFixtureLabel(survey.otherFixture, label),
                    })
                  }
                />
              </td>
              {([true, false] as const).map((connected) => (
                <td key={String(connected)} className={s.count}>
                  <CountInput
                    label={`${other.fixture.label}, ${location.label}, ${connected ? 'Oui' : 'Non'}`}
                    value={
                      (connected ? other.counts[at]?.connected : other.counts[at]?.notConnected) ??
                      null
                    }
                    disabled={disabled}
                    onValueChange={(count) =>
                      onChange?.({
                        ...survey,
                        otherFixture: setOtherFixtureCount(
                          survey.otherFixture,
                          { location: location.code, connected },
                          count,
                        ),
                      })
                    }
                  />
                </td>
              ))}
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  )
}

/** La case « Non » d'un point d'eau : le rappel des anomalies relevées, et le geste qui manque. */
function FlagCell({
  fixture,
  location,
  anomalies,
  toFlag,
  disabled,
  onFlag,
  label,
}: {
  fixture: string
  location: { code: string; label: string }
  anomalies: readonly { fixture?: string | null; fixtureLocation?: string | null }[]
  /** Encore un « Non » sans son anomalie — la règle de `fixturesToFlag`, que la transmission lit aussi. */
  toFlag: boolean
  disabled?: boolean
  onFlag?: (fixture: string, location: string) => void
  label: string
}) {
  const flagged = anomalies.filter(
    (anomaly) => anomaly.fixture === fixture && anomaly.fixtureLocation === location.code,
  ).length

  return (
    <span className={s.flag}>
      {flagged > 0 && (
        <Badge tone="bad">{flagged === 1 ? '1 anomalie' : `${String(flagged)} anomalies`}</Badge>
      )}
      {onFlag !== undefined && toFlag && (
        <Button
          size="sm"
          tone="neutral"
          disabled={disabled}
          aria-label={`Relever l’anomalie — ${label}, ${location.label}`}
          onClick={() => onFlag(fixture, location.code)}
        >
          <TriangleAlert size={16} aria-hidden />
          Relever l’anomalie
        </Button>
      )}
    </span>
  )
}
